import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ClipboardList, Plus, Calendar, ArrowLeft, User, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';

const TasksPage = () => {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchTasks();
  }, [filter]);

  const fetchTasks = async () => {
    try {
      const token = localStorage.getItem('token');
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await axios.get(`${API}/tasks${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTasks(response.data);
      setIsLoading(false);
    } catch (error) {
      toast.error('Failed to load tasks');
      setIsLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-500/20 text-yellow-400',
      scheduled: 'bg-blue-500/20 text-blue-400',
      in_progress: 'bg-purple-500/20 text-purple-400',
      completed: 'bg-green-500/20 text-green-400',
      cancelled: 'bg-red-500/20 text-red-400'
    };
    return colors[status] || 'bg-gray-500/20 text-gray-400';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-500/20 text-gray-400',
      normal: 'bg-blue-500/20 text-blue-400',
      high: 'bg-orange-500/20 text-orange-400',
      urgent: 'bg-red-500/20 text-red-400'
    };
    return colors[priority] || 'bg-gray-500/20 text-gray-400';
  };

  return (
    <AdminLayout>
      <Toaster />
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/admin/dashboard')}
              className="text-gray-400 hover:text-white hover:bg-gray-800"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-3xl font-bold text-white flex items-center gap-3">
                <ClipboardList className="w-8 h-8 text-orange-500" />
                Tasks
              </h1>
              <p className="text-gray-400 mt-2">Manage service tasks and assignments</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/admin/schedule')} variant="outline" className="bg-gray-800 border-gray-700 text-white hover:bg-gray-700">
              <Calendar className="w-4 h-4 mr-2" />
              Calendar View
            </Button>
            <Button onClick={() => navigate('/admin/tasks/create')} className="bg-orange-600 hover:bg-orange-700">
              <Plus className="w-4 h-4 mr-2" />
              Create Task
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          {['all', 'pending', 'scheduled', 'in_progress', 'completed'].map(status => (
            <Button
              key={status}
              size="sm"
              variant={filter === status ? "default" : "outline"}
              onClick={() => setFilter(status)}
              className={filter === status ? "bg-orange-600 hover:bg-orange-700" : "bg-gray-800 border-gray-700 text-gray-300 hover:bg-gray-700"}
            >
              {status.replace('_', ' ')}
            </Button>
          ))}
        </div>

        {/* Tasks List */}
        <div className="space-y-4">
          {tasks.map((task) => (
            <Card key={task.id} className="bg-gray-800 border-gray-700 hover:border-orange-500/50 transition-colors cursor-pointer" onClick={() => navigate(`/admin/tasks/${task.id}`)}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <CardTitle className="text-white">{task.title}</CardTitle>
                      <Badge className={getStatusColor(task.status)}>
                        {task.status.replace('_', ' ')}
                      </Badge>
                      <Badge className={getPriorityColor(task.priority)}>
                        {task.priority}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-400">{task.task_number} • {task.service_routine}</p>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 text-gray-400">
                      <MapPin className="w-4 h-4" />
                      <span>{task.client_name} - {task.site_name}</span>
                    </div>
                    {task.assigned_technician_name && (
                      <div className="flex items-center gap-2 text-gray-400">
                        <User className="w-4 h-4" />
                        <span>{task.assigned_technician_name}</span>
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    {task.scheduled_date && (
                      <p className="text-gray-400">
                        {new Date(task.scheduled_date).toLocaleDateString()} 
                        {task.scheduled_time_slot && ` • ${task.scheduled_time_slot}`}
                      </p>
                    )}
                    {task.assets && task.assets.length > 0 && (
                      <p className="text-xs text-gray-500 mt-1">{task.assets.length} asset(s)</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {tasks.length === 0 && !isLoading && (
          <Card className="bg-gray-800 border-gray-700 text-center p-12">
            <ClipboardList className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No tasks found</h3>
            <p className="text-gray-400 mb-6">Create your first task to get started</p>
            <Button onClick={() => navigate('/admin/tasks/create')} className="bg-orange-600 hover:bg-orange-700">
              <Plus className="w-4 h-4 mr-2" />
              Create First Task
            </Button>
          </Card>
        )}
      </div>
    </AdminLayout>
  );
};

export default TasksPage;
